<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">活动列表</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 个活动</span>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="活动名称">
            <el-input
              v-model="query.keyword"
              placeholder="名称关键词"
              clearable
              style="width: 180px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="活动类别">
            <el-select v-model="query.category" placeholder="全部" clearable style="width: 150px">
              <el-option v-for="d in categories" :key="d._id" :label="d.label" :value="String(d._id)" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 130px">
              <el-option v-for="s in ACTIVITY_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增活动</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <el-table-column prop="name" label="活动名称" min-width="220" show-overflow-tooltip/>
        <el-table-column label="活动类别" min-width="120">
          <template slot-scope="scope">{{ categoryLabel(scope.row.category) }}</template>
        </el-table-column>
        <el-table-column label="时间" min-width="200">
          <template slot-scope="scope">
            <div>{{ timeText(scope.row) }}</div>
            <div class="cell-muted cell-sub">{{ timeTypeLabel(scope.row.time_type) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="地点" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <div>{{ scope.row.place_name || '—' }}</div>
            <div class="cell-muted cell-sub">{{ regionText(scope.row) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="流程" width="90" align="center">
          <template slot-scope="scope">
            <span v-if="flowCount(scope.row)" class="flow-badge">{{ flowCount(scope.row) }} 天</span>
            <span v-else class="cell-muted">未编排</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="activityStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ activityStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updated_at" label="最近更新" width="150"/>
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <!-- 可流转状态：1 报名中 / 2 进行中 / 3 已结束；草稿只由新增表单「存草稿」产生，已存在活动不允许退回草稿 -->
            <el-dropdown
              class="status-dropdown"
              trigger="click"
              @command="cmd => handleStatusCommand(cmd, scope.row)"
            >
              <el-button type="text" size="mini">
                更新状态<i class="el-icon-arrow-down el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-for="s in statusOptions"
                  :key="s.value"
                  :command="s.value"
                  :disabled="s.value === scope.row.status"
                >{{ s.label }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <activity-form
      v-if="formVisible"
      :visible.sync="formVisible"
      :row="currentRow"
      :categories="categories"
      :time-type-list="ACTIVITY_TIME_TYPE"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
// 状态/时间类型的显示映射（前端展示用，随 mock 目录统一维护；数据已走后端接口）
import {
  ACTIVITY_STATUS, ACTIVITY_TIME_TYPE,
  activityStatusText, activityStatusTagType, activityTimeTypeLabel
} from '@/mock/activity'
import { getActivityList, getActivityDetail, deleteActivity, updateActivityStatus } from '@/api/activity'
import { getProvince, getCity, getArea } from '@/api/public'
import dictOptions from '@/mixins/dict-options'
import ActivityForm from './components/ActivityForm.vue'

export default {
  name: 'ActivityManage',
  components: { ActivityForm },
  mixins: [dictOptions],
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      categories: [],
      query: { keyword: '', category: '', status: '' },
      formVisible: false,
      currentRow: null,
      ACTIVITY_STATUS,
      // 「更新状态」下拉：不含草稿。草稿仅由新增表单「存草稿」产生，已存在活动不允许退回草稿
      statusOptions: ACTIVITY_STATUS.filter(s => s.value !== 0),
      ACTIVITY_TIME_TYPE
    }
  },
  created() {
    this.loadData()
    this.loadDictOptions({ activity_category: 'categories' })
  },
  methods: {
    // 列表：服务端分页 + 筛选（keyword 模糊 / category、status 精确）
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const keyword = (this.query.keyword || '').trim()
      if (keyword) params.keyword = keyword
      if (this.query.category) params.category = this.query.category
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status

      getActivityList(params).then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    // 列表只回 flow_days（流程天数），不带整个 flows
    flowCount(row) {
      return row.flow_days || 0
    },
    // 更新状态：下拉选中即提交（当前状态项禁用），改完刷新列表同步状态列
    handleStatusCommand(status, row) {
      const label = activityStatusText(status)
      this.$confirm(`确认将活动「${row.name}」状态更新为「${label}」？`, '状态更新确认', {
        type: 'warning',
        confirmButtonText: '确认更新'
      }).then(() => {
        updateActivityStatus(row.id, { status }).then(res => {
          if (res.code !== 200) return
          this.$message.success(`状态已更新为${label}`)
          this.loadData()
        })
      }).catch(() => {})
    },
    // 业务表存字典数字 _id（统一字符串口径比较）
    categoryLabel(value) {
      const item = this.categories.find(c => String(c._id) === String(value))
      return item ? item.label : (value || '—')
    },
    timeTypeLabel: activityTimeTypeLabel,
    activityStatusText,
    activityStatusTagType,
    regionText(row) {
      return [row.province_text, row.city_text, row.district_text].filter(Boolean).join(' / ') || '—'
    },
    timeText(row) {
      if (row.time_type === 1 && row.start_date && row.end_date) {
        return `${row.start_date} ~ ${row.end_date}`
      }
      return row.start_date || '—'
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', category: '', status: '' }
      this.page = 1
      this.loadData()
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handleAdd() {
      this.currentRow = null
      this.formVisible = true
    },
    // 列表不含 intro/flows，编辑前先拉详情再开表单
    handleEdit(row) {
      getActivityDetail(row.id).then(res => {
        if (res.code !== 200 || !res.data) return
        this.currentRow = res.data
        this.formVisible = true
      })
    },
    handleDelete(row) {
      this.$confirm(`确认删除活动「${row.name}」？`, '删除确认', { type: 'warning' }).then(() => {
        deleteActivity(row.id).then(res => {
          if (res.code !== 200) return
          this.$message.success('已删除')
          // 删掉当前页最后一条时回退一页
          if (this.list.length === 1 && this.page > 1) this.page -= 1
          this.loadData()
        })
      }).catch(() => {})
    },
    handleSaved() {
      this.formVisible = false
      this.loadData()
    }
  }
}
</script>

<style scoped>
.cell-sub {
  font-size: 12px;
  margin-top: 2px;
}
/* 表格操作列里 el-dropdown 与相邻文字按钮留出间距 */
.status-dropdown {
  margin: 0 10px;
}
.flow-badge {
  display: inline-block;
  padding: 1px 8px;
  font-size: 12px;
  color: var(--primary);
  background: var(--primary-300, #f7e4e1);
  border-radius: 10px;
}
</style>
